import PortalPrograms from '@classes/Programs'
import PortalStudentProfile from '@classes/StudentProfile'
import BlockedScheduling from '@components/BlockScheduling'
import EmptyLessons from '@components/EmptyLessons'
import EmptyFlexLessons from '@components/EmptyLessons/EmptyFlexLessons'
import WithUserTimeHOC, { WithUserTimeProps } from '@hocs/WithUserTime'
import { StoreProps } from '@interfaces/StoreState'
import NotEnoughLessonsRemaining from '@layouts/Scheduling/ScheduleOptions/NotEnoughLessonsRemaining'
import OptionBodyFlex from '@layouts/Scheduling/ScheduleOptions/OptionBodyFlex'
import ProgramIncorrectlySetup from '@layouts/Scheduling/ScheduleOptions/ProgramIncorrectlySetup'
import React from 'react'
import { useSelector } from 'react-redux'
import { Row } from 'react-styled-flexboxgrid'
import CardBulkBook from './CardBulkBook'
import CardGroupLeader from './CardGroupLeader'
import CardSingleBook from './CardSingleBook'
import InvalidProgramStatus from './InvalidProgramStatus'
import NonLeadGroupMember from './NonLeadGroupMember'
import ProgramExpired from './ProgramExpired'
import ScheduleOrientation from './ScheduleOrientation'
import { useIntl } from 'react-intl'
import CardPromoteFMI from './CardPromoteFMI'
import { LANGUAGE_LEVEL, PLACEMENT_TEST, PROGRAM_TYPES, VALID_PROGRAM_TYPES } from '@utils/constants'
import OnlinePlacementTest from '@components/OnlinePlacementTest'

const OptionsBody: React.FC<WithUserTimeProps & { programId: string }> = ({
  programId,
  isProgramContractExpired,
  getDateWithUserTimezone,
}) => {
  const intl = useIntl()
  const { StudentProgram, StudentProfile, StudentPrograms } = useSelector(({ userProfile }: StoreProps) => {
    const StudentPrograms = new PortalPrograms(
      userProfile?.info?.StudentProfile?.StudentProfileInfo?.ProgramStudentWrappers ?? []
    )
    return {
      StudentPrograms,
      StudentProgram: StudentPrograms.getProgram(programId),
      StudentProfile: new PortalStudentProfile(userProfile?.info?.StudentProfile?.StudentProfileInfo),
    }
  })

  const StudentProgramWithReg = StudentProfile.RegistrationPortalPrograms.getProgram(programId)
  const progWithReg = StudentProgramWithReg.program

  if (
    progWithReg &&
    StudentProgramWithReg.isBerlitzOnDemand1() &&
    progWithReg.LanguageLevel === LANGUAGE_LEVEL.NOT_SET &&
    VALID_PROGRAM_TYPES.includes(progWithReg.Status!) &&
    progWithReg.FromRegistration?.PlacementTestRequired === PLACEMENT_TEST.REQUIRED
  ) {
    return <OnlinePlacementTest showNotSetBanner />
  }

  if (PROGRAM_TYPES.ON_DEMAND.includes(StudentProgram.ProgramTypeDisplay ?? '')) {
    return (
      <InvalidProgramStatus
        {...StudentProgram.program}
        customTitle={intl.formatMessage({
          id: 'You do not need to book any lessons for this course.',
          defaultMessage: 'You do not need to book any lessons for this course.',
        })}
        customBody={intl.formatMessage({
          id: 'Please contact your learning center for more information, or raise a case using the button below.',
          defaultMessage:
            'Please contact your learning center for more information, or raise a case using the button below.',
        })}
      />
    )
  }

  if (StudentProgram.program) {
    if (StudentProgram.program.LessonsRemaining < 1) {
      return StudentProgram.isFlexProgram() ? (
        <EmptyFlexLessons top="md" programId={StudentProgram.program.ProgramId} />
      ) : (
        <EmptyLessons top="md" />
      )
    }

    if (!StudentProgram.enoughLessonLeftPerSession() && programId) {
      return <NotEnoughLessonsRemaining {...StudentProgram.program} />
    }

    if (StudentProgram.isFlexProgram()) {
      return <OptionBodyFlex StudentProgram={StudentProfile.Programs?.[programId]} />
    }

    if (StudentProgram.isProgramNonLeadGroupMember()) {
      return <NonLeadGroupMember {...StudentProgram.program} />
    }

    if (isProgramContractExpired(programId)) {
      return <ProgramExpired />
    }

    if (StudentProgram.isProgramBlocked()) {
      return <BlockedScheduling top="md" />
    }

    if (!StudentProgram.isProgramValidForScheduling() && programId) {
      return <InvalidProgramStatus {...StudentProgram.program} />
    }

    if (StudentProgram.isProgramForOrientation()) {
      return <ScheduleOrientation />
    }

    if (StudentPrograms.hasMoreInactiveAndNoActivePrograms(getDateWithUserTimezone) || !programId) {
      return (
        <InvalidProgramStatus
          {...StudentProgram.program}
          customTitle={intl.formatMessage({
            id: 'You cannot book lessons as you are no longer enrolled in an Active course.',
            defaultMessage: 'You cannot book lessons as you are no longer enrolled in an Active course.',
          })}
          customBody={intl.formatMessage({
            id: 'Please contact your learning centre for more information, or request support using the button below.',
            defaultMessage:
              'Please contact your learning centre for more information, or request support using the button below.',
          })}
        />
      )
    }

    if (StudentProgram.isProgramSetupCorrectly() && programId) {
      return (
        <Row>
          <CardSingleBook {...StudentProgram.program} />
          <CardBulkBook {...StudentProgram.program} />
          {StudentProgram.isFMIEnabled() ? (
            <>
              {StudentProgram?.hasFeature('SingleSessionScheduling') && <CardPromoteFMI promoteSingle={true} />}
              {StudentProgram?.hasFeature('BulkScheduling') && <CardPromoteFMI promoteSingle={false} />}
            </>
          ) : null}
          <CardGroupLeader {...StudentProgram.program} />
        </Row>
      )
    }
  }

  if (StudentProgram.program) {
    return <ProgramIncorrectlySetup {...StudentProgram.program} />
  }

  return null
}

export default WithUserTimeHOC(OptionsBody)
